import { useRecoilState } from "recoil";
import { HelpCentreContent, HelpCentreTimestamp } from "@interfaces/Interfaces";
import { auditHelpContentAtom, auditHelpTimestampAtom } from "@state/State";
import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CONFIG_BUCKET_BASE } from "../../Constants";

export interface UseAuditHelpData {
  content: HelpCentreContent[];
}

const fetchAuditHelpTimestamp = async (): Promise<HelpCentreTimestamp> => {
  const response: Response = await fetch(`${CONFIG_BUCKET_BASE}/audit-profile/timestamp.json`, {
    headers: {
      "Cache-Control": "no-cache",
    },
  });

  const timestamp = await response.json();
  return timestamp;
};

const fetchAuditHelpContent = async (): Promise<HelpCentreContent[]> => {
  const response: Response = await fetch(`${CONFIG_BUCKET_BASE}/audit-profile/auditHelp.json`, {
    headers: {
      "Cache-Control": "no-cache",
    },
  });

  const content = await response.json();
  return content;
};

export const useAuditHelp = (loadData?: boolean): UseAuditHelpData => {
  const [content, setContent] = useRecoilState<HelpCentreContent[]>(auditHelpContentAtom);
  const [timestamp, setTimestamp] = useRecoilState<HelpCentreTimestamp>(auditHelpTimestampAtom);

  const { data: fetchedTimestamp } = useQuery({
    queryKey: ["audit-help-timestamp"],
    queryFn: fetchAuditHelpTimestamp,
    enabled: Boolean(loadData),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  const shouldFetchContent = useMemo(() => {
    if (!fetchedTimestamp?.lastUpdatedTime) {
      return false;
    }
    return fetchedTimestamp.lastUpdatedTime > (timestamp.lastUpdatedTime || 0);
  }, [fetchedTimestamp, timestamp.lastUpdatedTime]);

  const { data: fetchedContent } = useQuery({
    queryKey: ["audit-help-content"],
    queryFn: fetchAuditHelpContent,
    enabled: Boolean(loadData && shouldFetchContent),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (fetchedTimestamp) {
      setTimestamp(fetchedTimestamp);
    }
  }, [fetchedTimestamp, setTimestamp]);

  useEffect(() => {
    if (fetchedContent) {
      setContent(fetchedContent);
    }
  }, [fetchedContent, setContent]);

  return {
    content,
  };
};
